import type { FeatureFlag } from '@sb/shared/feature';
import {
  hasAllPermissions,
  hasAllPermissionsForReports,
  hasPermission,
  type PermissionCheck,
  type PermissionOption,
  type PermissionState,
} from '@sb/shared/permission/util';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';

import type { AccessConstraint, AccessContext, PermissionCheckMode } from './access.model';

/** A constraint's feature flag(s) as a list (`[]` when it declares none). */
export function requiredFeatureFlags(constraint: AccessConstraint): FeatureFlag[] {
  const flag = constraint.featureFlag;
  if (flag === undefined) {
    return [];
  }
  return Array.isArray(flag) ? flag : [flag];
}

/** Whether the current user satisfies every dimension of `constraint`. Pure. */
export function canAccess(constraint: AccessConstraint, context: AccessContext): boolean {
  return (
    requiredFeatureFlags(constraint).every((flag) => context.isFeatureEnabled(flag)) &&
    meetsPlan(constraint, context.subscription) &&
    meetsPermission(constraint, context.permissionState)
  );
}

function meetsPlan(constraint: AccessConstraint, subscription: AccessContext['subscription']): boolean {
  return (
    !constraint.subscriptionPlan || hasAtLeastSubscriptionPlan(constraint.subscriptionPlan, subscription ?? undefined)
  );
}

function meetsPermission(constraint: AccessConstraint, permissionState: PermissionState): boolean {
  if (constraint.permission === undefined) {
    return true;
  }
  return checkPermission(constraint.permission, constraint.permissionCheck, permissionState);
}

function checkPermission(
  permission: PermissionOption,
  mode: PermissionCheckMode | undefined,
  permissionState: PermissionState,
): boolean {
  const check: PermissionCheck = { permissions: permission, userId: 'me', departments: 'any' };
  switch (mode) {
    case 'all':
      return hasAllPermissions(check, permissionState);
    case 'reports':
      return hasAllPermissionsForReports(check, permissionState);
    default:
      return hasPermission(check, permissionState);
  }
}
